import { Button, message, Modal, Tooltip } from "antd";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "utils/dayjs-config";
import CIQDatePicker from "components/custom-date-picker";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import {
  CalendarOutlined,
  CloseCircleFilled,
  ExclamationCircleOutlined,
  InfoCircleFilled
} from "@ant-design/icons";
import { MaterialCiqIcon } from "components/svg-icons";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { SidepanelStyles } from "constants/index";
import { findPreviousValidDate } from "./date-helpers";
import {
  buildWorkflowItemUpdate,
  categorizeItems,
  findRemainingWorkflowItem,
  forEachRemainingWorkflowItem,
  isDateValid,
  truncateWithEllipsis
} from "./helpers";
import type { CategorizedItems, UpdatedItems, WorkflowItem } from "./types";

interface BulkFeatureEditStatusProps {
  selectedRows: any[];
  /** Maps a raw grid row to the normalized WorkflowItem (identity for materials). */
  mapRowToWorkflowItem: (row: any) => WorkflowItem;
  entityLabelSingular: string;
  entityLabelPlural: string;
  icon?: ReactNode;
  requestSource?: string;
  /** Materials exclude items whose predecessors are not closed; packages do not. */
  enablePredecessorExclusion?: boolean;
  testIdPrefix?: string;
  showEdit: boolean;
  setShowEdit: (v: null) => void;
  onUpdateDone: () => void;
}

interface DateItemProps {
  item: WorkflowItem;
  onDateChange: (itemId: string, value: string) => void;
  value?: any;
  isInvalid?: boolean;
}

const tooltipContent = (items: WorkflowItem[]) => (
  <div className="flex flex-col gap-2 p-1">
    {items.map((item) => (
      <div
        key={item.id}
        style={{
          border: "1px solid var(--ciq-gray-200)",
          borderRadius: "6px",
          padding: "8px 12px",
          background: "white",
          fontSize: "14px",
          color: "var(--ciq-gray-975)",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          minWidth: "200px",
          maxWidth: "250px",
          cursor: "pointer",
          transition: "background-color 0.2s"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "var(--ciq-neutral-25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "white";
        }}
      >
        <Tooltip title={item.title}>{item.title}</Tooltip>
      </div>
    ))}
  </div>
);

function DateItem({ item, onDateChange, value, isInvalid }: DateItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDateChange = (date: any) => {
    onDateChange(item.id, date);
  };

  const handleCalendarClick = () => {
    setIsExpanded(true);
  };

  const maxTitleLength = !isExpanded && !value ? 35 : 20;
  const shouldShowDatePicker = isExpanded || value;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 20px"
      }}
    >
      <Tooltip title={item.title}>
        <span style={{ color: "var(--ciq-gray-900)", fontSize: "14px" }}>
          {truncateWithEllipsis(item.title, maxTitleLength)}
        </span>
      </Tooltip>

      {shouldShowDatePicker ? (
        <CIQDatePicker
          className={`w-[140px] ${isInvalid ? "border-red-500" : ""}`}
          placeholder="Select date"
          format={DATE_FORMAT_MMDDYYYY}
          value={value}
          onChange={handleDateChange}
          allowClear={false}
          disabledDate={(current) =>
            current && current.isAfter(dayjs().endOf("day"))
          }
          style={isInvalid ? { borderColor: "var(--ciq-error-500)" } : {}}
          onBlur={() => {
            if (!value) {
              setIsExpanded(false);
            }
          }}
        />
      ) : (
        <CalendarOutlined
          onClick={handleCalendarClick}
          style={{
            fontSize: "16px",
            color: "var(--ciq-gray-900)",
            cursor: "pointer",
            padding: "8px",
            width: "25px",
            textAlign: "center"
          }}
        />
      )}
    </div>
  );
}

interface StepCardProps {
  item: WorkflowItem;
  groupItems: WorkflowItem[];
  updatedItems: UpdatedItems;
  onDateChange: (itemId: string, value: string) => void;
  icon: ReactNode;
}

function StepCard({
  item,
  groupItems,
  updatedItems,
  onDateChange,
  icon
}: StepCardProps) {
  return (
    <div
      style={{
        marginBottom: "16px",
        border: "1px solid var(--ciq-gray-200)",
        borderRadius: "8px",
        backgroundColor: "white"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          backgroundColor: "var(--ciq-gray-50)",
          opacity: "75%",
          padding: "16px"
        }}
      >
        <div
          style={{
            width: "24px",
            height: "24px",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "bold",
            marginRight: "12px"
          }}
        >
          <div
            className="flex items-center justify-center rounded"
            style={{
              padding: 3,
              backgroundColor: "var(--ciq-teal-200)",
              color: "black"
            }}
          >
            {icon}
            {groupItems.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "14px", color: "var(--ciq-gray-900)" }}>
            Set Actual Date for
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "var(--ciq-blue-600)"
            }}
          >
            {item.current_milestone_name}
          </div>
        </div>
      </div>

      <div>
        {groupItems.map((value) => {
          const hasDate = updatedItems[value.id]?.actualDate;
          const isInvalid =
            hasDate && value.previous_milestone_date
              ? !dayjs(updatedItems[value.id].actualDate).isSameOrAfter(
                  dayjs(value.previous_milestone_date),
                  "day"
                )
              : false;

          return (
            <DateItem
              key={value.id}
              item={value}
              onDateChange={onDateChange}
              value={
                updatedItems[value.id]?.actualDate
                  ? dayjs(updatedItems[value.id].actualDate)
                  : null
              }
              isInvalid={isInvalid}
            />
          );
        })}
      </div>
    </div>
  );
}

function BulkFeatureEditStatus(props: BulkFeatureEditStatusProps) {
  const {
    onUpdateDone,
    selectedRows,
    showEdit,
    setShowEdit,
    mapRowToWorkflowItem,
    entityLabelSingular,
    entityLabelPlural,
    icon = (
      <MaterialCiqIcon
        size={20}
        className="fill-ciq-gray-900 pr-[5px]"
        opacity="1"
      />
    ),
    requestSource = "LOG_PAGE",
    enablePredecessorExclusion = false,
    testIdPrefix = "bulkfeatureeditstatus"
  } = props;

  const [categorizedItems, setCategorizedItems] = useState<CategorizedItems>({
    completed: [],
    predecessorsIncomplete: [],
    remaining: {}
  });
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [reviewEditExpanded, setReviewEditExpanded] = useState(false);
  const [mainDate, setMainDate] = useState<string | null>(null);
  const [overrideCount, setOverrideCount] = useState(0);
  const [updatedItems, setUpdatedItems] = useState<UpdatedItems>({});

  const { gqlClientForProject, calHolidaysWeekends }: TProjectContext =
    useContext(ProjectContext);
  const [mutationApplyWorkflowInstanceDataChanges] = useCIQMutation(
    APPLY_WORKFLOW_MUTATION,
    {
      client: gqlClientForProject
    }
  );

  const workflowItems = useMemo(
    () => selectedRows.map(mapRowToWorkflowItem),
    [selectedRows, mapRowToWorkflowItem]
  );

  const snapToValidDate = (date: any) =>
    findPreviousValidDate(dayjs(date), calHolidaysWeekends);

  const pluralize = (count: number) =>
    count === 1 ? entityLabelSingular : entityLabelPlural;

  const showBothSections =
    categorizedItems.completed.length > 0 &&
    categorizedItems.predecessorsIncomplete.length > 0;
  const showOneSection =
    !showBothSections &&
    (categorizedItems.completed.length > 0 ||
      categorizedItems.predecessorsIncomplete.length > 0);

  /*
   * rc-picker emits null whenever the field is emptied — not only via the clear
   * "x" (now off, see the pickers below) but also when the text is selected and
   * deleted, which no `allowClear` setting prevents. dayjs(null) is an *invalid*
   * dayjs: an object, therefore truthy, so it slipped past every `if (!date)`
   * guard downstream and was formatted into the literal string
   * "Invalid DateT13:23:24+00:00" — shown in the input and, for items with no
   * previous milestone date, submittable as an actual_date. This date is
   * required, so ignore the clear and keep the value already set.
   */
  const handleMainDateChange = (date: any) => {
    if (!date?.isValid?.()) return;
    const validDate = snapToValidDate(date);
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);
    setMainDate(dateValue);
    const updated: UpdatedItems = {};

    forEachRemainingWorkflowItem(categorizedItems.remaining, (item) => {
      updated[item.id] = buildWorkflowItemUpdate(item, dateValue);
    });

    setUpdatedItems((prev) => {
      const newState = { ...prev, ...updated };
      const overrides = Object.keys(newState).filter(
        (id) => dateValue && newState[id].actualDate !== dateValue
      ).length;
      setOverrideCount(overrides);
      return newState;
    });
  };

  // Same null-clear guard as handleMainDateChange above.
  const handleIndividualDateChange = (itemId: string, date: any) => {
    if (!date?.isValid?.()) return;
    const validDate = snapToValidDate(date);
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);

    const targetItem = findRemainingWorkflowItem(
      categorizedItems.remaining,
      itemId
    );

    setUpdatedItems((prev) => {
      const newUpdated = { ...prev };
      newUpdated[itemId] = buildWorkflowItemUpdate(targetItem!, dateValue);

      const overrides = Object.keys(newUpdated).filter(
        (id) => mainDate && newUpdated[id].actualDate !== mainDate
      ).length;
      setOverrideCount(overrides);

      return newUpdated;
    });
  };

  useEffect(() => {
    setCategorizedItems(
      categorizeItems(workflowItems, enablePredecessorExclusion)
    );
  }, [workflowItems, enablePredecessorExclusion]);

  useEffect(() => {
    const today = DateUtils.dateTimeObj();
    const validDate = findPreviousValidDate(today, calHolidaysWeekends);
    const dateValue = DateUtils.formatDateWithLunchTime(validDate);
    setMainDate(dateValue);

    const updated: UpdatedItems = {};
    forEachRemainingWorkflowItem(categorizedItems.remaining, (item) => {
      updated[item.id] = buildWorkflowItemUpdate(item, dateValue);
    });
    setUpdatedItems(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorizedItems.remaining]);

  const hasAnyDateEntry = Object.keys(updatedItems).length > 0;

  const calculateInvalidCount = () => {
    let invalidCount = 0;
    Object.entries(updatedItems).forEach(([itemId, data]) => {
      const targetItem = findRemainingWorkflowItem(
        categorizedItems.remaining,
        itemId
      );
      if (
        targetItem &&
        !isDateValid(data.actualDate, targetItem.previous_milestone_date)
      ) {
        invalidCount += 1;
      }
    });
    return invalidCount;
  };

  const currentInvalidCount = calculateInvalidCount();
  const hasValidEntries = hasAnyDateEntry && currentInvalidCount === 0;

  useEffect(() => {
    if (currentInvalidCount > 0) {
      setReviewEditExpanded(true);
    }
  }, [currentInvalidCount]);

  const onConfirm = async () => {
    setIsConfirmDisabled(true);
    const updates = Object.entries(updatedItems).map(([itemId, data]) => ({
      feature_instance_id: itemId,
      workflow_instance_id: data.workflow_instance_id,
      milestones: [
        {
          id: data.id,
          actual_date: data.actualDate
        }
      ]
    }));

    const response = await mutationApplyWorkflowInstanceDataChanges({
      variables: {
        input: updates,
        metadata: {
          request_source: requestSource
        }
      }
    });

    if (response.success) {
      setShowEdit(null);
      message.success("Updated successfully");
      setTimeout(() => {
        onUpdateDone();
      }, 0);
    }
    setIsConfirmDisabled(false);
  };

  const toggleReviewEdit = () => {
    setReviewEditExpanded((prev) => !prev);
  };

  return (
    <Modal
      title={[
        <div key="title" data-testid={`${testIdPrefix}-modal`}>
          <span>Bulk Edit Status</span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal">
            {selectedRows.length} Selected {entityLabelPlural}
          </span>
        </div>
      ]}
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      footer={[
        <div className="flex w-full space-x-3 justify-end" key={103}>
          <Button
            onClick={() => {
              onUpdateDone();
            }}
            key={104}
            disabled={false}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={!hasValidEntries || isConfirmDisabled}
            loading={false}
            onClick={onConfirm}
            key={105}
            data-testid="apply-button"
          >
            Confirm
          </Button>
        </div>
      ]}
      open={showEdit}
      onCancel={() => {
        setShowEdit(null);
        onUpdateDone();
      }}
      destroyOnHidden
      data-testid={`${testIdPrefix}-modal-root`}
    >
      <div
        className="body-content-wrapper"
        data-testid={`${testIdPrefix}-body`}
      >
        {(showOneSection || showBothSections) && (
          <div
            style={{
              backgroundColor: "var(--ciq-blue-100)",
              border: "1px solid var(--ciq-blue-300)",
              borderRadius: "6px",
              padding: "12px 10px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "flex-start"
            }}
            data-testid={`${testIdPrefix}-info-section`}
          >
            <div>
              <InfoCircleFilled className="text-ciq-blue-700 pr-[5px]" />
            </div>
            {showBothSections && (
              <div>
                <div
                  style={{
                    color: "var(--ciq-blue-600)",
                    fontSize: "12px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    paddingTop: "2px"
                  }}
                >
                  Status of the following will be affected:
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "16px",
                    color: "var(--ciq-gray-900)",
                    fontSize: "12px",
                    lineHeight: "1.5"
                  }}
                >
                  <li data-testid={`${testIdPrefix}-completed-list`}>
                    <Tooltip title={tooltipContent(categorizedItems.completed)}>
                      <strong className="underline">
                        {categorizedItems.completed.length}
                      </strong>{" "}
                    </Tooltip>
                    Completed {pluralize(categorizedItems.completed.length)}{" "}
                    will not be updated.
                  </li>
                  <li data-testid={`${testIdPrefix}-predecessors-list`}>
                    <Tooltip
                      title={tooltipContent(
                        categorizedItems.predecessorsIncomplete
                      )}
                    >
                      <strong className="underline">
                        {categorizedItems.predecessorsIncomplete.length}
                      </strong>{" "}
                    </Tooltip>
                    {pluralize(categorizedItems.predecessorsIncomplete.length)}{" "}
                    with linked to Predecessors which are not closed will not be
                    updated.
                  </li>
                </ul>
              </div>
            )}
            {showOneSection &&
              (categorizedItems.completed.length > 0 ? (
                <div data-testid={`${testIdPrefix}-completed-section`}>
                  <Tooltip title={tooltipContent(categorizedItems.completed)}>
                    <strong className="underline">
                      {categorizedItems.completed.length}
                    </strong>{" "}
                  </Tooltip>
                  Completed {entityLabelPlural} will not be updated.
                </div>
              ) : (
                <div data-testid={`${testIdPrefix}-predecessors-section`}>
                  <Tooltip
                    title={tooltipContent(
                      categorizedItems.predecessorsIncomplete
                    )}
                  >
                    <strong className="underline">
                      {categorizedItems.predecessorsIncomplete.length}
                    </strong>{" "}
                  </Tooltip>
                  {entityLabelPlural} with linked to Predecessors which are not
                  closed will not be updated.
                </div>
              ))}
          </div>
        )}
        <div
          className="mb-1 font-semibold"
          data-testid="set-actual-date-next-milestone"
        >
          <span className="tracking-wide" data-testid="selected-count-text">
            Set Actual Date of Next Milestone
          </span>
        </div>
        <div className="mb-3 text-xs">
          <span style={{ opacity: "45%" }}>
            Date entered applies to all selected {entityLabelPlural}. You can
            change the date of individual {entityLabelPlural} in the ‘Review &
            Edit’ section.
          </span>
        </div>
        <div
          className="w-[95%] pb-[15px]"
          data-testid={`${testIdPrefix}-main-datepicker-section`}
        >
          <span className="mb-3 text-xs" style={{ opacity: "85%" }}>
            Milestone Completed On
          </span>
          <CIQDatePicker
            className="w-full"
            placeholder="Select date"
            format={DATE_FORMAT_MMDDYYYY}
            value={mainDate ? dayjs(mainDate) : null}
            onChange={(date: any) => handleMainDateChange(date)}
            allowClear={false}
            placement="topLeft"
            disabledDate={(current) =>
              current && current.isAfter(dayjs().endOf("day"))
            }
            data-testid={`${testIdPrefix}-main-datepicker`}
          />
          {overrideCount > 0 && (
            <span
              style={{ fontSize: "12px" }}
              data-testid={`${testIdPrefix}-override-count`}
            >
              <strong>{overrideCount}</strong>{" "}
              <span style={{ opacity: "45%" }}>
                {pluralize(overrideCount)} have different dates
              </span>
            </span>
          )}
          {currentInvalidCount > 0 && (
            <div
              style={{
                backgroundColor: "var(--ciq-red-50)",
                border: "1px solid var(--ciq-red-400)",
                borderRadius: "6px",
                padding: "12px",
                marginTop: "8px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px"
              }}
              data-testid={`${testIdPrefix}-invalid-warning`}
            >
              <CloseCircleFilled
                className="text-red-500 pt-1 hover:text-red-500"
                style={{ width: "14px", height: "14px" }}
              />
              <div style={{ flex: 1 }}>
                <span
                  style={{ fontSize: "12px", color: "var(--ciq-gray-950)" }}
                >
                  This date cannot be added to{" "}
                  <strong>
                    {currentInvalidCount} {pluralize(currentInvalidCount)}
                  </strong>{" "}
                  as it is earlier than its previous Milestone&apos;s Actual
                  date.
                  <br />
                  Please set another date for them below.
                </span>
              </div>
            </div>
          )}
        </div>
        <hr style={{ opacity: "45%" }} />
        <div className="pt-[10px]">
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              onClick={toggleReviewEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleReviewEdit();
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={reviewEditExpanded}
              aria-label="Toggle review and edit section"
              data-testid={`${testIdPrefix}-clickable-div`}
            >
              <div
                style={{
                  margin: 0,
                  fontSize: "14px",
                  flex: 1
                }}
              >
                <p
                  className="mb-1 font-semibold"
                  data-testid={`${testIdPrefix}-review-edit-title`}
                >
                  Review & Edit
                </p>

                <p
                  style={{
                    opacity: "45%",
                    fontSize: "12px",
                    margin: "0 0 16px 0"
                  }}
                  data-testid={`${testIdPrefix}-review-edit-desc`}
                >
                  Edit or Set a different Actual Date for each{" "}
                  {entityLabelSingular}.
                </p>
              </div>
              {currentInvalidCount > 0 && (
                <ExclamationCircleOutlined
                  className="text-red-500 pt-1 hover:text-red-500 mb-[28px]"
                  style={{ width: "14px", height: "14px" }}
                  data-testid={`${testIdPrefix}-review-edit-warning`}
                />
              )}
              <i
                className="fas fa-chevron-up"
                style={{
                  marginLeft: "8px",
                  marginBottom: "20px",
                  transform: reviewEditExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.3s"
                }}
                data-testid={`${testIdPrefix}-chevron`}
              />
            </div>

            {reviewEditExpanded && (
              <div data-testid={`${testIdPrefix}-review-edit-section`}>
                {Object.entries(categorizedItems.remaining).map(
                  ([templateName, milestones]) => (
                    <div
                      key={templateName || "no-template"}
                      data-testid={`${testIdPrefix}-template-${
                        templateName || "no-template"
                      }`}
                    >
                      {templateName !== "" && (
                        <div
                          style={{
                            fontSize: "14px",
                            margin: "0 0 10px 0"
                          }}
                          data-testid={`${testIdPrefix}-template-title`}
                        >
                          {templateName}
                        </div>
                      )}
                      <ul className="pl-[0px]">
                        {Object.entries(milestones).map(
                          ([milestoneName, items]) => (
                            <StepCard
                              key={milestoneName}
                              item={items[0]}
                              groupItems={items}
                              updatedItems={updatedItems}
                              onDateChange={handleIndividualDateChange}
                              icon={icon}
                            />
                          )
                        )}
                      </ul>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default BulkFeatureEditStatus;
